import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDischargeField,
  addDischargeArrayItem,
  removeDischargeArrayItem,
} from "../../../../Redux/actions/dischargeActions";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Modal, Form, Row, Col, Input, DatePicker, message, Select } from "antd";
import moment from "moment";
import MedicalHistoryService from "../../../../Services/MedicalHistoryService";

export default function ClinicalDetails() {
  const dispatch = useDispatch();
  const chiefComplaint = useSelector(
    (state) => state.discharge?.chiefComplaint || ""
  );
  const dischargeAllergy = useSelector(
    (state) => state.discharge?.dischargeAllergy || []
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [medicineAllergies, setMedicineAllergies] = useState([]);
  const [foodAllergies, setFoodAllergies] = useState([]);
  const [chemicalAllergies, setChemicalAllergies] = useState([]);
  const [otherAllergies, setOtherAllergies] = useState([]);

  const fetchFoodAllergies = async (filterPayload) => {
    const payload = { status: 1 };
    const finalPayload = filterPayload ? { ...payload, ...filterPayload } : payload;
    try {
      const response = await MedicalHistoryService.foodAllergyList(finalPayload);
      setFoodAllergies(response.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMedicineAllergies = async (filterPayload) => {
    const payload = { status: 1 };
    const finalPayload = filterPayload ? { ...payload, ...filterPayload } : payload;
    try {
      const response = await MedicalHistoryService.medicineAllergyList(finalPayload);
      setMedicineAllergies(response.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchChemicalAllergies = async (filterPayload) => {
    const payload = { status: 1 };
    const finalPayload = filterPayload ? { ...payload, ...filterPayload } : payload;
    try {
      const response = await MedicalHistoryService.chemicalAllergyList(finalPayload);
      setChemicalAllergies(response.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchOtherAllergies = async (filterPayload) => {
    const payload = { status: 1 };
    const finalPayload = filterPayload ? { ...payload, ...filterPayload } : payload;
    try {
      const response = await MedicalHistoryService.otherAllergyList(finalPayload);
      setOtherAllergies(response.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddAllergyClick = () => {
    form.resetFields();
    fetchMedicineAllergies();
    fetchFoodAllergies();
    fetchChemicalAllergies();
    fetchOtherAllergies();
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onFinish = (values) => {
    if (
      !values.drugAllergy &&
      !values.foodAllergy &&
      !values.chemicalAllergy &&
      !values.otherAllergy
    ) {
      message.error("Please fill any allergy field");
      return;
    }

    if (!values.givenDate) {
      message.error("Please Enter Date");
      return;
    }

    const newAllergy = {
      drugAllergy: Array.isArray(values.drugAllergy) ? values.drugAllergy[0] : values.drugAllergy || "",
      foodAllergy: Array.isArray(values.foodAllergy) ? values.foodAllergy[0] : values.foodAllergy || "",
      chemicalAllergy: Array.isArray(values.chemicalAllergy) ? values.chemicalAllergy[0] : values.chemicalAllergy || "",
      otherAllergy: Array.isArray(values.otherAllergy) ? values.otherAllergy[0] : values.otherAllergy || "",
      givenDate: values.givenDate ? values.givenDate.format("YYYY-MM-DD") : "",
    };
    newAllergy.allergyName = [
      newAllergy.drugAllergy,
      newAllergy.foodAllergy,
      newAllergy.chemicalAllergy,
      newAllergy.otherAllergy,
    ]
      .filter(Boolean)
      .join(", ");

    dispatch(addDischargeArrayItem("dischargeAllergy", newAllergy));
    setIsModalOpen(false);
  };

  const handleRemoveAllergy = (index) => {
    dispatch(removeDischargeArrayItem("dischargeAllergy", index));
  };

  const disabledFutureDate = (current) => {
    return current && current > moment().endOf("day");
  };

  const handleDrugSearch = (value) => {
    if (value) fetchMedicineAllergies({ description: value });
    else fetchMedicineAllergies();
  };

  const handleFoodSearch = (value) => {
    if (value) fetchFoodAllergies({ description: value });
    else fetchFoodAllergies();
  };

  const handleChemSearch = (value) => {
    if (value) fetchChemicalAllergies({ description: value });
    else fetchChemicalAllergies();
  };

  const handleOthSearch = (value) => {
    if (value) fetchOtherAllergies({ description: value });
    else fetchOtherAllergies();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow space-y-4">
      <h3 className="text-sm font-bold text-teal-700 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-teal-600"></span>
        3. Clinical Details
      </h3>

      <div>
        <label className="block text-xs font-semibold mb-2 text-gray-700">
          Chief Complaints <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={chiefComplaint}
          onChange={(e) =>
            dispatch(setDischargeField("chiefComplaint", e.target.value))
          }
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition"
          placeholder="e.g. Fever, weakness and dehydration"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-gray-700">
            Allergies ({dischargeAllergy.length})
          </label>
          <button
            type="button"
            onClick={handleAddAllergyClick}
            className="flex items-center text-white cursor-pointer gap-1 text-[11px] font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-2 py-0.5 rounded transition"
          >
            <FiPlus /> Add Allergy
          </button>
        </div>

        <div className="space-y-2.5">
          {dischargeAllergy.map((item, index) => (
            <div
              key={index}
              className="p-3 border border-gray-200 rounded-md bg-gray-50 flex justify-between items-start"
            >
              <div className="flex flex-col space-y-1">
                {item.drugAllergy && (
                  <span className="text-xs text-gray-800">
                    <b>Drug:</b> {item.drugAllergy}
                  </span>
                )}
                {item.foodAllergy && (
                  <span className="text-xs text-gray-800">
                    <b>Food:</b> {item.foodAllergy}
                  </span>
                )}
                {item.chemicalAllergy && (
                  <span className="text-xs text-gray-800">
                    <b>Chemical:</b> {item.chemicalAllergy}
                  </span>
                )}
                {item.otherAllergy && (
                  <span className="text-xs text-gray-800">
                    <b>Other:</b> {item.otherAllergy}
                  </span>
                )}
                {item.givenDate && (
                  <span className="text-xs text-gray-500">
                    <b>Date:</b> {item.givenDate}
                  </span>
                )}
                {item.allergyName &&
                  !item.drugAllergy &&
                  !item.foodAllergy &&
                  !item.chemicalAllergy &&
                  !item.otherAllergy && (
                    <span className="text-xs text-gray-800">
                      <b>Name:</b> {item.allergyName}
                    </span>
                  )}
                {item.reaction && (
                  <span className="text-xs text-gray-500">
                    <b>Reaction:</b> {item.reaction}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAllergy(index)}
                className="text-red-500 bg-white bg-white hover:text-red-700 p-1.5 rounded shadow-sm border border-gray-200 transition"
                title="Delete allergy"
              >
                <FiTrash2 className="text-sm" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Modal
        title="Add Allergy Details"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={20}>
            <Col span={12}>
              <Form.Item label="Drug Allergies" name="drugAllergy">
                <Select
                  mode="tags"
                  maxCount={1}
                  showSearch
                  onSearch={handleDrugSearch}
                  placeholder="Select"
                  options={medicineAllergies.map((item) => ({
                    label: item.description,
                    value: item.description,
                    key: item.id,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Food Allergies" name="foodAllergy">
                <Select
                  mode="tags"
                  maxCount={1}
                  showSearch
                  onSearch={handleFoodSearch}
                  placeholder="Select"
                  options={foodAllergies.map((item) => ({
                    label: item.description,
                    value: item.description,
                    key: item.id,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Chemical Allergies" name="chemicalAllergy">
                <Select
                  mode="tags"
                  maxCount={1}
                  showSearch
                  onSearch={handleChemSearch}
                  placeholder="Select"
                  options={chemicalAllergies.map((item) => ({
                    label: item.description,
                    value: item.description,
                    key: item.id,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Other Allergies" name="otherAllergy">
                <Select
                  mode="tags"
                  maxCount={1}
                  showSearch
                  onSearch={handleOthSearch}
                  placeholder="Select"
                  options={otherAllergies.map((item) => ({
                    label: item.description,
                    value: item.description,
                    key: item.id,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Date <span style={{ color: "#FF000F" }}>*</span>
                  </span>
                }
                name="givenDate"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select Date"
                  format="YYYY-MM-DD"
                  disabledDate={disabledFutureDate}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="flex items-center justify-end mt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded text-gray-700 bg-gray-100 hover:bg-gray-200 text-sm px-6 py-1.5 mr-3 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-brand-primaryColor rounded px-5 py-1.5 text-white text-sm bg-teal-600 hover:bg-teal-700 transition"
            >
              Submit
            </button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
